/**
 * BulkImportModal - Modal for bulk importing broadcast schedules from CSV
 */
import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "../../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "../utils";

export const BulkImportModal = ({ 
  isOpen, 
  onClose, 
  onImportComplete 
}) => {
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleClose = () => {
    setImportResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const headers = [
      'title', 'sport', 'scheduled_date', 'scheduled_time', 'channel_id', 
      'description', 'home_team_name', 'home_team_abbr', 'home_team_color',
      'away_team_name', 'away_team_abbr', 'away_team_color', 'youtube_url'
    ];
    const sampleRow = [
      'Eagles vs Lions', 'Football', '2026-02-15', '19:00', 'channel1',
      'Regional rivalry game', 'Eagles', 'EGL', '#004C54',
      'Lions', 'LNS', '#C8102E', ''
    ];
    
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'broadcast_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/broadcasts/schedule/bulk-import`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(res.data);
      
      if (res.data.imported > 0 && onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error("Import error:", error);
      setImportResult({
        imported: 0,
        failed: 1,
        errors: [error.response?.data?.detail || "Import failed. Please check your CSV format."]
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 border border-white/10 w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-orange-500" />
            <h3 className="font-secondary text-lg font-bold uppercase text-white">Bulk Import Schedule</h3>
          </div>
          <button onClick={handleClose} className="text-zinc-500 hover:text-white">
            ×
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {!importResult ? (
            <>
              <p className="text-zinc-400 text-sm">
                Upload a CSV file to import multiple games at once. Download the template to see the expected format.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={downloadTemplate}
                  className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-4 py-3 h-auto rounded-none border border-white/10"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-white/20 hover:border-orange-500/50 p-6 text-center transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleImportFile}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">
                    {importing ? 'Importing...' : 'Click to upload CSV'}
                  </p>
                  <p className="text-zinc-500 text-xs">
                    or drag and drop your file here
                  </p>
                </label>
              </div>
              
              <div className="bg-zinc-800/50 p-3 text-xs text-zinc-500">
                <p className="font-bold text-zinc-400 mb-1">Required columns:</p>
                <p>title, sport, scheduled_date (YYYY-MM-DD), scheduled_time (HH:MM)</p>
                <p className="font-bold text-zinc-400 mt-2 mb-1">Optional columns:</p>
                <p>channel_id, description, home_team_name, home_team_abbr, away_team_name, away_team_abbr</p>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {/* Import Result */}
              <div className={`p-4 flex items-center gap-3 ${
                importResult.imported > 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
              }`}>
                {importResult.imported > 0 ? (
                  <CheckCircle className="w-8 h-8 text-green-500" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-red-500" />
                )}
                <div>
                  <p className={`font-bold ${importResult.imported > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {importResult.imported > 0 ? 'Import Complete!' : 'Import Failed'}
                  </p>
                  <p className="text-zinc-400 text-sm">
                    {importResult.imported} games imported
                    {importResult.failed > 0 && `, ${importResult.failed} failed`}
                  </p>
                </div>
              </div>
              
              {/* Errors */}
              {importResult.errors?.length > 0 && (
                <div className="bg-zinc-800 p-3 max-h-48 overflow-y-auto">
                  <p className="text-xs font-bold text-zinc-400 mb-2">Errors ({importResult.total_errors || importResult.errors.length}):</p>
                  {importResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-red-400 mb-1">{err}</p>
                  ))}
                </div>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setImportResult(null)}
                  className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-4 py-2 h-auto rounded-none"
                >
                  Import More
                </Button>
                <Button
                  onClick={handleClose}
                  className="flex-1 bg-white text-black hover:bg-zinc-200 font-secondary text-xs px-4 py-2 h-auto rounded-none"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BulkImportModal;
